/**
 * Generated by orval v6.16.0 🍺
 * Do not edit manually.
 * FastAPI
 * OpenAPI spec version: 0.1.0
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseMutationOptions,
  QueryFunction,
  MutationFunction,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import type {
  UserSearchActivityStats,
  HTTPValidationError,
  ClinicalInsightsUserSettingsBackfillByIdResponse,
  ErrorResponse,
  InfoPaneResponse,
  ExampleInstructionRecord,
  InstructionRecord,
  InstructionCreateResponse,
  PracticeInstructionCreateRequest,
  UserInstructionEnabledRequest,
  UserInstructionCreateRequest,
  InstructionUpdateRequest,
  ClinicalInsights,
  ClinicalInsightsIndexRequest,
  SearchInitiateResponse,
  ReportChatRequest,
  ReportSummaryResponse,
  SearchChartRequest,
  SearchStatusResponse,
  FeedbackRecord,
  FeedbackCreateResponse,
  FeedbackCreateRequest,
  FeedbackUpdateRequest,
  PreventCalculateResponse,
  PreventCalculateRequest,
  AIUserSettingsRecord,
  AIUserSettingsUpsertRequest,
  InsertJsonBlobRequest,
  WaitForWorkflowTestingWorkflowsInstanceIdWaitGetParams,
} from './models';
import { customInstance } from '../core/customInstance';
import type { ErrorType } from '../core/customInstance';

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Get chart search activity statistics for the authenticated user.

Returns activity metrics including:
- Total searches (all time)
- First and last search timestamps
- Searches in last 7 and 30 days
- Activity tier (never/inactive/active)

The activity tier is used by the frontend to determine which UI experience
to show (onboarding for new users, re-engagement for lapsed users, etc.).
 * @summary Get User Activity
 */
export const getUserActivityActivityGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UserSearchActivityStats>(
    { url: `/gw/chart-assist-api/activity`, method: 'get', signal },
    options,
  );
};

export const getGetUserActivityActivityGetQueryKey = () =>
  [`/gw/chart-assist-api/activity`] as const;

export const getGetUserActivityActivityGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserActivityActivityGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getUserActivityActivityGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getUserActivityActivityGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserActivityActivityGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserActivityActivityGet>>> = ({
    signal,
  }) => getUserActivityActivityGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUserActivityActivityGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserActivityActivityGet>>
>;
export type GetUserActivityActivityGetQueryError = ErrorType<unknown>;

/**
 * @summary Get User Activity
 */
export const useGetUserActivityActivityGet = <
  TData = Awaited<ReturnType<typeof getUserActivityActivityGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getUserActivityActivityGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUserActivityActivityGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Actor Deactivation
 */
export const actorDeactivationActorsActorTypeNameActorIdDelete = (
  actorTypeName: string,
  actorId: string,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/actors/${actorTypeName}/${actorId}`, method: 'delete' },
    options,
  );
};

export const getActorDeactivationActorsActorTypeNameActorIdDeleteMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorDeactivationActorsActorTypeNameActorIdDelete>>,
    TError,
    { actorTypeName: string; actorId: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof actorDeactivationActorsActorTypeNameActorIdDelete>>,
  TError,
  { actorTypeName: string; actorId: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof actorDeactivationActorsActorTypeNameActorIdDelete>>,
    { actorTypeName: string; actorId: string }
  > = (props) => {
    const { actorTypeName, actorId } = props ?? {};

    return actorDeactivationActorsActorTypeNameActorIdDelete(
      actorTypeName,
      actorId,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type ActorDeactivationActorsActorTypeNameActorIdDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof actorDeactivationActorsActorTypeNameActorIdDelete>>
>;

export type ActorDeactivationActorsActorTypeNameActorIdDeleteMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Actor Deactivation
 */
export const useActorDeactivationActorsActorTypeNameActorIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorDeactivationActorsActorTypeNameActorIdDelete>>,
    TError,
    { actorTypeName: string; actorId: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getActorDeactivationActorsActorTypeNameActorIdDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Actor Reminder
 */
export const actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut = (
  actorTypeName: string,
  actorId: string,
  reminderName: string,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/actors/${actorTypeName}/${actorId}/method/remind/${reminderName}`,
      method: 'put',
    },
    options,
  );
};

export const getActorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePutMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut>
      >,
      TError,
      { actorTypeName: string; actorId: string; reminderName: string },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut>>,
    TError,
    { actorTypeName: string; actorId: string; reminderName: string },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut>
      >,
      { actorTypeName: string; actorId: string; reminderName: string }
    > = (props) => {
      const { actorTypeName, actorId, reminderName } = props ?? {};

      return actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut(
        actorTypeName,
        actorId,
        reminderName,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type ActorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePutMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut>>
  >;

export type ActorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePutMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Actor Reminder
 */
export const useActorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePut>>,
    TError,
    { actorTypeName: string; actorId: string; reminderName: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getActorReminderActorsActorTypeNameActorIdMethodRemindReminderNamePutMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Actor Timer
 */
export const actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut = (
  actorTypeName: string,
  actorId: string,
  timerName: string,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/actors/${actorTypeName}/${actorId}/method/timer/${timerName}`,
      method: 'put',
    },
    options,
  );
};

export const getActorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePutMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut>>,
    TError,
    { actorTypeName: string; actorId: string; timerName: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut>>,
  TError,
  { actorTypeName: string; actorId: string; timerName: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut>>,
    { actorTypeName: string; actorId: string; timerName: string }
  > = (props) => {
    const { actorTypeName, actorId, timerName } = props ?? {};

    return actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut(
      actorTypeName,
      actorId,
      timerName,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type ActorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePutMutationResult = NonNullable<
  Awaited<ReturnType<typeof actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut>>
>;

export type ActorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePutMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Actor Timer
 */
export const useActorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePut>>,
    TError,
    { actorTypeName: string; actorId: string; timerName: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getActorTimerActorsActorTypeNameActorIdMethodTimerTimerNamePutMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Actor Method
 */
export const actorMethodActorsActorTypeNameActorIdMethodMethodNamePut = (
  actorTypeName: string,
  actorId: string,
  methodName: string,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/actors/${actorTypeName}/${actorId}/method/${methodName}`,
      method: 'put',
    },
    options,
  );
};

export const getActorMethodActorsActorTypeNameActorIdMethodMethodNamePutMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorMethodActorsActorTypeNameActorIdMethodMethodNamePut>>,
    TError,
    { actorTypeName: string; actorId: string; methodName: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof actorMethodActorsActorTypeNameActorIdMethodMethodNamePut>>,
  TError,
  { actorTypeName: string; actorId: string; methodName: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof actorMethodActorsActorTypeNameActorIdMethodMethodNamePut>>,
    { actorTypeName: string; actorId: string; methodName: string }
  > = (props) => {
    const { actorTypeName, actorId, methodName } = props ?? {};

    return actorMethodActorsActorTypeNameActorIdMethodMethodNamePut(
      actorTypeName,
      actorId,
      methodName,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type ActorMethodActorsActorTypeNameActorIdMethodMethodNamePutMutationResult = NonNullable<
  Awaited<ReturnType<typeof actorMethodActorsActorTypeNameActorIdMethodMethodNamePut>>
>;

export type ActorMethodActorsActorTypeNameActorIdMethodMethodNamePutMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Actor Method
 */
export const useActorMethodActorsActorTypeNameActorIdMethodMethodNamePut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof actorMethodActorsActorTypeNameActorIdMethodMethodNamePut>>,
    TError,
    { actorTypeName: string; actorId: string; methodName: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getActorMethodActorsActorTypeNameActorIdMethodMethodNamePutMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Backfill a single user_settings record to Kafka by ID.

This endpoint publishes historical user settings as events to ensure
downstream Snowflake analytics tables are synchronized with the database.

This is an admin-only endpoint requiring a JWT token with `hippo_admin` scope.

Responses:
- 200 OK: Successfully published to Kafka
- 403 Forbidden: Admin access required
- 404 Not Found: User setting not found or deleted
- 500 Internal Server Error: Database or Kafka publishing failure
 * @summary Backfill single user setting by ID
 */
export const adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost = (
  id: number,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<ClinicalInsightsUserSettingsBackfillByIdResponse>(
    { url: `/gw/chart-assist-api/admin/backfill/user-settings/${id}`, method: 'post' },
    options,
  );
};

export const getAdminBackfillUserSettingByIdAdminBackfillUserSettingsIdPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost>>,
  TError,
  { id: number },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost>>,
    { id: number }
  > = (props) => {
    const { id } = props ?? {};

    return adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type AdminBackfillUserSettingByIdAdminBackfillUserSettingsIdPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost>>
>;

export type AdminBackfillUserSettingByIdAdminBackfillUserSettingsIdPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Backfill single user setting by ID
 */
export const useAdminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof adminBackfillUserSettingByIdAdminBackfillUserSettingsIdPost>>,
    TError,
    { id: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getAdminBackfillUserSettingByIdAdminBackfillUserSettingsIdPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Dapr Config
 */
export const daprConfigDaprConfigGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/dapr/config`, method: 'get', signal },
    options,
  );
};

export const getDaprConfigDaprConfigGetQueryKey = () =>
  [`/gw/chart-assist-api/dapr/config`] as const;

export const getDaprConfigDaprConfigGetQueryOptions = <
  TData = Awaited<ReturnType<typeof daprConfigDaprConfigGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof daprConfigDaprConfigGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof daprConfigDaprConfigGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDaprConfigDaprConfigGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof daprConfigDaprConfigGet>>> = ({
    signal,
  }) => daprConfigDaprConfigGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type DaprConfigDaprConfigGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof daprConfigDaprConfigGet>>
>;
export type DaprConfigDaprConfigGetQueryError = ErrorType<unknown>;

/**
 * @summary Dapr Config
 */
export const useDaprConfigDaprConfigGet = <
  TData = Awaited<ReturnType<typeof daprConfigDaprConfigGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof daprConfigDaprConfigGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getDaprConfigDaprConfigGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getGwChartAssistApiDocs = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>({ url: `/gw/chart-assist-api/docs`, method: 'get', signal }, options);
};

export const getGetGwChartAssistApiDocsQueryKey = () => [`/gw/chart-assist-api/docs`] as const;

export const getGetGwChartAssistApiDocsQueryOptions = <
  TData = Awaited<ReturnType<typeof getGwChartAssistApiDocs>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getGwChartAssistApiDocs>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getGwChartAssistApiDocs>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGwChartAssistApiDocsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGwChartAssistApiDocs>>> = ({
    signal,
  }) => getGwChartAssistApiDocs(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetGwChartAssistApiDocsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGwChartAssistApiDocs>>
>;
export type GetGwChartAssistApiDocsQueryError = ErrorType<unknown>;

export const useGetGwChartAssistApiDocs = <
  TData = Awaited<ReturnType<typeof getGwChartAssistApiDocs>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getGwChartAssistApiDocs>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetGwChartAssistApiDocsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Live
 */
export const getLiveHealthLiveGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    { url: `/gw/chart-assist-api/health/live`, method: 'get', signal },
    options,
  );
};

export const getGetLiveHealthLiveGetQueryKey = () => [`/gw/chart-assist-api/health/live`] as const;

export const getGetLiveHealthLiveGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getLiveHealthLiveGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getLiveHealthLiveGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getLiveHealthLiveGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetLiveHealthLiveGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getLiveHealthLiveGet>>> = ({ signal }) =>
    getLiveHealthLiveGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetLiveHealthLiveGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getLiveHealthLiveGet>>
>;
export type GetLiveHealthLiveGetQueryError = ErrorType<unknown>;

/**
 * @summary Get Live
 */
export const useGetLiveHealthLiveGet = <
  TData = Awaited<ReturnType<typeof getLiveHealthLiveGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getLiveHealthLiveGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetLiveHealthLiveGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Ready
 */
export const getReadyHealthReadyGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    { url: `/gw/chart-assist-api/health/ready`, method: 'get', signal },
    options,
  );
};

export const getGetReadyHealthReadyGetQueryKey = () =>
  [`/gw/chart-assist-api/health/ready`] as const;

export const getGetReadyHealthReadyGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getReadyHealthReadyGet>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getReadyHealthReadyGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getReadyHealthReadyGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetReadyHealthReadyGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getReadyHealthReadyGet>>> = ({ signal }) =>
    getReadyHealthReadyGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetReadyHealthReadyGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReadyHealthReadyGet>>
>;
export type GetReadyHealthReadyGetQueryError = ErrorType<ErrorResponse>;

/**
 * @summary Get Ready
 */
export const useGetReadyHealthReadyGet = <
  TData = Awaited<ReturnType<typeof getReadyHealthReadyGet>>,
  TError = ErrorType<ErrorResponse>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getReadyHealthReadyGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetReadyHealthReadyGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Healthz
 */
export const healthzHealthzGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/healthz`, method: 'get', signal },
    options,
  );
};

export const getHealthzHealthzGetQueryKey = () => [`/gw/chart-assist-api/healthz`] as const;

export const getHealthzHealthzGetQueryOptions = <
  TData = Awaited<ReturnType<typeof healthzHealthzGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof healthzHealthzGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof healthzHealthzGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthzHealthzGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof healthzHealthzGet>>> = ({ signal }) =>
    healthzHealthzGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type HealthzHealthzGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof healthzHealthzGet>>
>;
export type HealthzHealthzGetQueryError = ErrorType<unknown>;

/**
 * @summary Healthz
 */
export const useHealthzHealthzGet = <
  TData = Awaited<ReturnType<typeof healthzHealthzGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof healthzHealthzGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getHealthzHealthzGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Return content for the Clinical Insights info pane.
 * @summary Get Info Pane
 */
export const getInfoPaneInfoGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<InfoPaneResponse>(
    { url: `/gw/chart-assist-api/info`, method: 'get', signal },
    options,
  );
};

export const getGetInfoPaneInfoGetQueryKey = () => [`/gw/chart-assist-api/info`] as const;

export const getGetInfoPaneInfoGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getInfoPaneInfoGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getInfoPaneInfoGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getInfoPaneInfoGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetInfoPaneInfoGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getInfoPaneInfoGet>>> = ({ signal }) =>
    getInfoPaneInfoGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetInfoPaneInfoGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getInfoPaneInfoGet>>
>;
export type GetInfoPaneInfoGetQueryError = ErrorType<unknown>;

/**
 * @summary Get Info Pane
 */
export const useGetInfoPaneInfoGet = <
  TData = Awaited<ReturnType<typeof getInfoPaneInfoGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getInfoPaneInfoGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetInfoPaneInfoGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Get up to 10 example instructions for display to users.
 * @summary Get Example Instructions
 */
export const getExampleInstructionsInstructionsExamplesGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<ExampleInstructionRecord[]>(
    { url: `/gw/chart-assist-api/instructions/examples`, method: 'get', signal },
    options,
  );
};

export const getGetExampleInstructionsInstructionsExamplesGetQueryKey = () =>
  [`/gw/chart-assist-api/instructions/examples`] as const;

export const getGetExampleInstructionsInstructionsExamplesGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetExampleInstructionsInstructionsExamplesGetQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>
  > = ({ signal }) => getExampleInstructionsInstructionsExamplesGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetExampleInstructionsInstructionsExamplesGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>
>;
export type GetExampleInstructionsInstructionsExamplesGetQueryError = ErrorType<unknown>;

/**
 * @summary Get Example Instructions
 */
export const useGetExampleInstructionsInstructionsExamplesGet = <
  TData = Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getExampleInstructionsInstructionsExamplesGet>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetExampleInstructionsInstructionsExamplesGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Get all practice instructions for a specific practice.
 * @summary Get Practice Instructions
 */
export const getPracticeInstructionsInstructionsPracticePracticeIdGet = (
  practiceId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<InstructionRecord[]>(
    { url: `/gw/chart-assist-api/instructions/practice/${practiceId}`, method: 'get', signal },
    options,
  );
};

export const getGetPracticeInstructionsInstructionsPracticePracticeIdGetQueryKey = (
  practiceId: number,
) => [`/gw/chart-assist-api/instructions/practice/${practiceId}`] as const;

export const getGetPracticeInstructionsInstructionsPracticePracticeIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  practiceId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPracticeInstructionsInstructionsPracticePracticeIdGetQueryKey(practiceId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>
  > = ({ signal }) =>
    getPracticeInstructionsInstructionsPracticePracticeIdGet(practiceId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!practiceId, ...queryOptions };
};

export type GetPracticeInstructionsInstructionsPracticePracticeIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>
>;
export type GetPracticeInstructionsInstructionsPracticePracticeIdGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Practice Instructions
 */
export const useGetPracticeInstructionsInstructionsPracticePracticeIdGet = <
  TData = Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  practiceId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPracticeInstructionsInstructionsPracticePracticeIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPracticeInstructionsInstructionsPracticePracticeIdGetQueryOptions(
    practiceId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Create a new practice-level instruction.
 * @summary Create Practice Instruction
 */
export const createPracticeInstructionInstructionsPracticePracticeIdPost = (
  practiceId: number,
  practiceInstructionCreateRequest: PracticeInstructionCreateRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<InstructionCreateResponse>(
    {
      url: `/gw/chart-assist-api/instructions/practice/${practiceId}`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: practiceInstructionCreateRequest,
    },
    options,
  );
};

export const getCreatePracticeInstructionInstructionsPracticePracticeIdPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPracticeInstructionInstructionsPracticePracticeIdPost>>,
    TError,
    { practiceId: number; data: PracticeInstructionCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPracticeInstructionInstructionsPracticePracticeIdPost>>,
  TError,
  { practiceId: number; data: PracticeInstructionCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPracticeInstructionInstructionsPracticePracticeIdPost>>,
    { practiceId: number; data: PracticeInstructionCreateRequest }
  > = (props) => {
    const { practiceId, data } = props ?? {};

    return createPracticeInstructionInstructionsPracticePracticeIdPost(
      practiceId,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type CreatePracticeInstructionInstructionsPracticePracticeIdPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPracticeInstructionInstructionsPracticePracticeIdPost>>
>;
export type CreatePracticeInstructionInstructionsPracticePracticeIdPostMutationBody =
  PracticeInstructionCreateRequest;
export type CreatePracticeInstructionInstructionsPracticePracticeIdPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Create Practice Instruction
 */
export const useCreatePracticeInstructionInstructionsPracticePracticeIdPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPracticeInstructionInstructionsPracticePracticeIdPost>>,
    TError,
    { practiceId: number; data: PracticeInstructionCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getCreatePracticeInstructionInstructionsPracticePracticeIdPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Delete a practice instruction by soft-deleting it.
 * @summary Delete Practice Instruction
 */
export const deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete = (
  practiceId: number,
  instructionId: number,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/instructions/practice/${practiceId}/${instructionId}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDeleteMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<
          typeof deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete
        >
      >,
      TError,
      { practiceId: number; instructionId: number },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete>
    >,
    TError,
    { practiceId: number; instructionId: number },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<
          typeof deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete
        >
      >,
      { practiceId: number; instructionId: number }
    > = (props) => {
      const { practiceId, instructionId } = props ?? {};

      return deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete(
        practiceId,
        instructionId,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type DeletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDeleteMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete>
    >
  >;

export type DeletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDeleteMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Delete Practice Instruction
 */
export const useDeletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof deletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDelete>
    >,
    TError,
    { practiceId: number; instructionId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getDeletePracticeInstructionInstructionsPracticePracticeIdInstructionIdDeleteMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};

/**
 * Get all applicable instructions for a specific user.
 * @summary Get User Instructions
 */
export const getUserInstructionsInstructionsUserUserIdGet = (
  userId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<InstructionRecord[]>(
    { url: `/gw/chart-assist-api/instructions/user/${userId}`, method: 'get', signal },
    options,
  );
};

export const getGetUserInstructionsInstructionsUserUserIdGetQueryKey = (userId: number) =>
  [`/gw/chart-assist-api/instructions/user/${userId}`] as const;

export const getGetUserInstructionsInstructionsUserUserIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  userId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserInstructionsInstructionsUserUserIdGetQueryKey(userId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>
  > = ({ signal }) => getUserInstructionsInstructionsUserUserIdGet(userId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!userId, ...queryOptions };
};

export type GetUserInstructionsInstructionsUserUserIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>
>;
export type GetUserInstructionsInstructionsUserUserIdGetQueryError = ErrorType<HTTPValidationError>;

/**
 * @summary Get User Instructions
 */
export const useGetUserInstructionsInstructionsUserUserIdGet = <
  TData = Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  userId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUserInstructionsInstructionsUserUserIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUserInstructionsInstructionsUserUserIdGetQueryOptions(userId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Delete/disable an instruction for a specific user.
Complex logic based on instruction type and enforcement.
 * @summary Delete User Instruction
 */
export const deleteUserInstructionInstructionsUserUserIdInstructionIdDelete = (
  userId: number,
  instructionId: number,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    { url: `/gw/chart-assist-api/instructions/user/${userId}/${instructionId}`, method: 'delete' },
    options,
  );
};

export const getDeleteUserInstructionInstructionsUserUserIdInstructionIdDeleteMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUserInstructionInstructionsUserUserIdInstructionIdDelete>>,
    TError,
    { userId: number; instructionId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUserInstructionInstructionsUserUserIdInstructionIdDelete>>,
  TError,
  { userId: number; instructionId: number },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUserInstructionInstructionsUserUserIdInstructionIdDelete>>,
    { userId: number; instructionId: number }
  > = (props) => {
    const { userId, instructionId } = props ?? {};

    return deleteUserInstructionInstructionsUserUserIdInstructionIdDelete(
      userId,
      instructionId,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserInstructionInstructionsUserUserIdInstructionIdDeleteMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof deleteUserInstructionInstructionsUserUserIdInstructionIdDelete>>
  >;

export type DeleteUserInstructionInstructionsUserUserIdInstructionIdDeleteMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Delete User Instruction
 */
export const useDeleteUserInstructionInstructionsUserUserIdInstructionIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUserInstructionInstructionsUserUserIdInstructionIdDelete>>,
    TError,
    { userId: number; instructionId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getDeleteUserInstructionInstructionsUserUserIdInstructionIdDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Update enabled status for a practice instruction for a specific user.
Allows users to enable/disable practice instructions for themselves.
 * @summary Update User Instruction Enabled
 */
export const updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch = (
  userId: number,
  instructionId: number,
  userInstructionEnabledRequest: UserInstructionEnabledRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/instructions/user/${userId}/${instructionId}`,
      method: 'patch',
      headers: { 'Content-Type': 'application/json' },
      data: userInstructionEnabledRequest,
    },
    options,
  );
};

export const getUpdateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatchMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch>
      >,
      TError,
      { userId: number; instructionId: number; data: UserInstructionEnabledRequest },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch>
    >,
    TError,
    { userId: number; instructionId: number; data: UserInstructionEnabledRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch>
      >,
      { userId: number; instructionId: number; data: UserInstructionEnabledRequest }
    > = (props) => {
      const { userId, instructionId, data } = props ?? {};

      return updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch(
        userId,
        instructionId,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type UpdateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatchMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch>>
  >;
export type UpdateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatchMutationBody =
  UserInstructionEnabledRequest;
export type UpdateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatchMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Update User Instruction Enabled
 */
export const useUpdateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof updateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatch>
    >,
    TError,
    { userId: number; instructionId: number; data: UserInstructionEnabledRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getUpdateUserInstructionEnabledInstructionsUserUserIdInstructionIdPatchMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Create a new user-specific instruction.
 * @summary Create User Instruction
 */
export const createUserInstructionInstructionsUsersUserIdPost = (
  userId: number,
  userInstructionCreateRequest: UserInstructionCreateRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<InstructionCreateResponse>(
    {
      url: `/gw/chart-assist-api/instructions/users/${userId}`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: userInstructionCreateRequest,
    },
    options,
  );
};

export const getCreateUserInstructionInstructionsUsersUserIdPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUserInstructionInstructionsUsersUserIdPost>>,
    TError,
    { userId: number; data: UserInstructionCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createUserInstructionInstructionsUsersUserIdPost>>,
  TError,
  { userId: number; data: UserInstructionCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createUserInstructionInstructionsUsersUserIdPost>>,
    { userId: number; data: UserInstructionCreateRequest }
  > = (props) => {
    const { userId, data } = props ?? {};

    return createUserInstructionInstructionsUsersUserIdPost(userId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserInstructionInstructionsUsersUserIdPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createUserInstructionInstructionsUsersUserIdPost>>
>;
export type CreateUserInstructionInstructionsUsersUserIdPostMutationBody =
  UserInstructionCreateRequest;
export type CreateUserInstructionInstructionsUsersUserIdPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Create User Instruction
 */
export const useCreateUserInstructionInstructionsUsersUserIdPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createUserInstructionInstructionsUsersUserIdPost>>,
    TError,
    { userId: number; data: UserInstructionCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getCreateUserInstructionInstructionsUsersUserIdPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Update an existing instruction by ID.
 * @summary Update Instruction
 */
export const updateInstructionInstructionsInstructionIdPatch = (
  instructionId: number,
  instructionUpdateRequest: InstructionUpdateRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/instructions/${instructionId}`,
      method: 'patch',
      headers: { 'Content-Type': 'application/json' },
      data: instructionUpdateRequest,
    },
    options,
  );
};

export const getUpdateInstructionInstructionsInstructionIdPatchMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateInstructionInstructionsInstructionIdPatch>>,
    TError,
    { instructionId: number; data: InstructionUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateInstructionInstructionsInstructionIdPatch>>,
  TError,
  { instructionId: number; data: InstructionUpdateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateInstructionInstructionsInstructionIdPatch>>,
    { instructionId: number; data: InstructionUpdateRequest }
  > = (props) => {
    const { instructionId, data } = props ?? {};

    return updateInstructionInstructionsInstructionIdPatch(instructionId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateInstructionInstructionsInstructionIdPatchMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateInstructionInstructionsInstructionIdPatch>>
>;
export type UpdateInstructionInstructionsInstructionIdPatchMutationBody = InstructionUpdateRequest;
export type UpdateInstructionInstructionsInstructionIdPatchMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Update Instruction
 */
export const useUpdateInstructionInstructionsInstructionIdPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateInstructionInstructionsInstructionIdPatch>>,
    TError,
    { instructionId: number; data: InstructionUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getUpdateInstructionInstructionsInstructionIdPatchMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Endpoint that serves Prometheus metrics.
 * @summary Metrics
 */
export const metricsMetricsGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/metrics`, method: 'get', signal },
    options,
  );
};

export const getMetricsMetricsGetQueryKey = () => [`/gw/chart-assist-api/metrics`] as const;

export const getMetricsMetricsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof metricsMetricsGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof metricsMetricsGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof metricsMetricsGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getMetricsMetricsGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof metricsMetricsGet>>> = ({ signal }) =>
    metricsMetricsGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type MetricsMetricsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof metricsMetricsGet>>
>;
export type MetricsMetricsGetQueryError = ErrorType<unknown>;

/**
 * @summary Metrics
 */
export const useMetricsMetricsGet = <
  TData = Awaited<ReturnType<typeof metricsMetricsGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof metricsMetricsGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getMetricsMetricsGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getGwChartAssistApiOpenapiJson = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    { url: `/gw/chart-assist-api/openapi.json`, method: 'get', signal },
    options,
  );
};

export const getGetGwChartAssistApiOpenapiJsonQueryKey = () =>
  [`/gw/chart-assist-api/openapi.json`] as const;

export const getGetGwChartAssistApiOpenapiJsonQueryOptions = <
  TData = Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetGwChartAssistApiOpenapiJsonQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>> = ({
    signal,
  }) => getGwChartAssistApiOpenapiJson(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetGwChartAssistApiOpenapiJsonQueryResult = NonNullable<
  Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>
>;
export type GetGwChartAssistApiOpenapiJsonQueryError = ErrorType<unknown>;

export const useGetGwChartAssistApiOpenapiJson = <
  TData = Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getGwChartAssistApiOpenapiJson>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetGwChartAssistApiOpenapiJsonQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Get clinical insights for a patient.
 * @summary Get Clinical Insights
 */
export const getClinicalInsightsPatientsPatientIdClinicalInsightsGet = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<ClinicalInsights>(
    { url: `/gw/chart-assist-api/patients/${patientId}/clinical-insights`, method: 'get', signal },
    options,
  );
};

export const getGetClinicalInsightsPatientsPatientIdClinicalInsightsGetQueryKey = (
  patientId: number,
) => [`/gw/chart-assist-api/patients/${patientId}/clinical-insights`] as const;

export const getGetClinicalInsightsPatientsPatientIdClinicalInsightsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetClinicalInsightsPatientsPatientIdClinicalInsightsGetQueryKey(patientId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>
  > = ({ signal }) =>
    getClinicalInsightsPatientsPatientIdClinicalInsightsGet(patientId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!patientId, ...queryOptions };
};

export type GetClinicalInsightsPatientsPatientIdClinicalInsightsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>
>;
export type GetClinicalInsightsPatientsPatientIdClinicalInsightsGetQueryError = ErrorType<
  ErrorResponse | HTTPValidationError
>;

/**
 * @summary Get Clinical Insights
 */
export const useGetClinicalInsightsPatientsPatientIdClinicalInsightsGet = <
  TData = Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getClinicalInsightsPatientsPatientIdClinicalInsightsGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetClinicalInsightsPatientsPatientIdClinicalInsightsGetQueryOptions(
    patientId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Clinical Insights Index Progress
 */
export const getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/clinical-insights-index`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGetQueryKey =
  (patientId: number) =>
    [`/gw/chart-assist-api/patients/${patientId}/clinical-insights-index`] as const;

export const getGetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet>
    >,
    TError = ErrorType<HTTPValidationError | ErrorResponse>,
  >(
    patientId: number,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<
            typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet
          >
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof customInstance>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet>
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGetQueryKey(
        patientId,
      );

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet>
      >
    > = ({ signal }) =>
      getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet(
        patientId,
        requestOptions,
        signal,
      );

    return { queryKey, queryFn, enabled: !!patientId, ...queryOptions };
  };

export type GetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet>
    >
  >;
export type GetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGetQueryError =
  ErrorType<HTTPValidationError | ErrorResponse>;

/**
 * @summary Get Clinical Insights Index Progress
 */
export const useGetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet = <
  TData = Awaited<
    ReturnType<typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet>
  >,
  TError = ErrorType<HTTPValidationError | ErrorResponse>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGet>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetClinicalInsightsIndexProgressPatientsPatientIdClinicalInsightsIndexGetQueryOptions(
      patientId,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Create Clinical Insights Index
 */
export const createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost = (
  patientId: number,
  clinicalInsightsIndexRequest: ClinicalInsightsIndexRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/clinical-insights-index`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: clinicalInsightsIndexRequest,
    },
    options,
  );
};

export const getCreateClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPostMutationOptions =
  <TError = ErrorType<HTTPValidationError | ErrorResponse>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost>
      >,
      TError,
      { patientId: number; data: ClinicalInsightsIndexRequest },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost>
    >,
    TError,
    { patientId: number; data: ClinicalInsightsIndexRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost>
      >,
      { patientId: number; data: ClinicalInsightsIndexRequest }
    > = (props) => {
      const { patientId, data } = props ?? {};

      return createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost(
        patientId,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type CreateClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPostMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost>
    >
  >;
export type CreateClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPostMutationBody =
  ClinicalInsightsIndexRequest;
export type CreateClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPostMutationError =
  ErrorType<HTTPValidationError | ErrorResponse>;

/**
 * @summary Create Clinical Insights Index
 */
export const useCreateClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost = <
  TError = ErrorType<HTTPValidationError | ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof createClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPost>
    >,
    TError,
    { patientId: number; data: ClinicalInsightsIndexRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getCreateClinicalInsightsIndexPatientsPatientIdClinicalInsightsIndexPostMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};

/**
 * Start a report chat session. Returns a searchId for streaming.
 * @summary Initiate Report Chat
 */
export const initiateReportChatPatientsPatientIdReportsReportIdChatPost = (
  patientId: number,
  reportId: number,
  reportChatRequest: ReportChatRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<SearchInitiateResponse>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/reports/${reportId}/chat`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: reportChatRequest,
    },
    options,
  );
};

export const getInitiateReportChatPatientsPatientIdReportsReportIdChatPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof initiateReportChatPatientsPatientIdReportsReportIdChatPost>>,
    TError,
    { patientId: number; reportId: number; data: ReportChatRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof initiateReportChatPatientsPatientIdReportsReportIdChatPost>>,
  TError,
  { patientId: number; reportId: number; data: ReportChatRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof initiateReportChatPatientsPatientIdReportsReportIdChatPost>>,
    { patientId: number; reportId: number; data: ReportChatRequest }
  > = (props) => {
    const { patientId, reportId, data } = props ?? {};

    return initiateReportChatPatientsPatientIdReportsReportIdChatPost(
      patientId,
      reportId,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type InitiateReportChatPatientsPatientIdReportsReportIdChatPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof initiateReportChatPatientsPatientIdReportsReportIdChatPost>>
>;
export type InitiateReportChatPatientsPatientIdReportsReportIdChatPostMutationBody =
  ReportChatRequest;
export type InitiateReportChatPatientsPatientIdReportsReportIdChatPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Initiate Report Chat
 */
export const useInitiateReportChatPatientsPatientIdReportsReportIdChatPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof initiateReportChatPatientsPatientIdReportsReportIdChatPost>>,
    TError,
    { patientId: number; reportId: number; data: ReportChatRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getInitiateReportChatPatientsPatientIdReportsReportIdChatPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Return the OCR summary for a single report.

If no cached summary exists, attempts just-in-time summarization by
fetching the report's files from Hippo and scheduling the OCR workflow.
This handles the case where a report was created before OCR indexing
was enabled for the practice.
 * @summary Get Report Summary
 */
export const getReportSummaryPatientsPatientIdReportsReportIdSummaryGet = (
  patientId: number,
  reportId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<ReportSummaryResponse>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/reports/${reportId}/summary`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetReportSummaryPatientsPatientIdReportsReportIdSummaryGetQueryKey = (
  patientId: number,
  reportId: number,
) => [`/gw/chart-assist-api/patients/${patientId}/reports/${reportId}/summary`] as const;

export const getGetReportSummaryPatientsPatientIdReportsReportIdSummaryGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  reportId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetReportSummaryPatientsPatientIdReportsReportIdSummaryGetQueryKey(patientId, reportId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>
  > = ({ signal }) =>
    getReportSummaryPatientsPatientIdReportsReportIdSummaryGet(
      patientId,
      reportId,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(patientId && reportId), ...queryOptions };
};

export type GetReportSummaryPatientsPatientIdReportsReportIdSummaryGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>
>;
export type GetReportSummaryPatientsPatientIdReportsReportIdSummaryGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Report Summary
 */
export const useGetReportSummaryPatientsPatientIdReportsReportIdSummaryGet = <
  TData = Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  reportId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getReportSummaryPatientsPatientIdReportsReportIdSummaryGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetReportSummaryPatientsPatientIdReportsReportIdSummaryGetQueryOptions(
    patientId,
    reportId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Kick off a chart search.

Returns a *searchId* UUID which the client can poll for status.
 * @summary Initiate Chart Search
 */
export const initiateChartSearchPatientsPatientIdSearchPost = (
  patientId: number,
  searchChartRequest: SearchChartRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<SearchInitiateResponse>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/search`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: searchChartRequest,
    },
    options,
  );
};

export const getInitiateChartSearchPatientsPatientIdSearchPostMutationOptions = <
  TError = ErrorType<HTTPValidationError | ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof initiateChartSearchPatientsPatientIdSearchPost>>,
    TError,
    { patientId: number; data: SearchChartRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof initiateChartSearchPatientsPatientIdSearchPost>>,
  TError,
  { patientId: number; data: SearchChartRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof initiateChartSearchPatientsPatientIdSearchPost>>,
    { patientId: number; data: SearchChartRequest }
  > = (props) => {
    const { patientId, data } = props ?? {};

    return initiateChartSearchPatientsPatientIdSearchPost(patientId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type InitiateChartSearchPatientsPatientIdSearchPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof initiateChartSearchPatientsPatientIdSearchPost>>
>;
export type InitiateChartSearchPatientsPatientIdSearchPostMutationBody = SearchChartRequest;
export type InitiateChartSearchPatientsPatientIdSearchPostMutationError = ErrorType<
  HTTPValidationError | ErrorResponse
>;

/**
 * @summary Initiate Chart Search
 */
export const useInitiateChartSearchPatientsPatientIdSearchPost = <
  TError = ErrorType<HTTPValidationError | ErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof initiateChartSearchPatientsPatientIdSearchPost>>,
    TError,
    { patientId: number; data: SearchChartRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions = getInitiateChartSearchPatientsPatientIdSearchPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Return the current status (in_progress/complete/error) of a chart search.
 * @summary Poll Chart Search Status
 */
export const pollChartSearchStatusPatientsPatientIdSearchSearchIdGet = (
  patientId: number,
  searchId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<SearchStatusResponse>(
    { url: `/gw/chart-assist-api/patients/${patientId}/search/${searchId}`, method: 'get', signal },
    options,
  );
};

export const getPollChartSearchStatusPatientsPatientIdSearchSearchIdGetQueryKey = (
  patientId: number,
  searchId: string,
) => [`/gw/chart-assist-api/patients/${patientId}/search/${searchId}`] as const;

export const getPollChartSearchStatusPatientsPatientIdSearchSearchIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  patientId: number,
  searchId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPollChartSearchStatusPatientsPatientIdSearchSearchIdGetQueryKey(patientId, searchId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>
  > = ({ signal }) =>
    pollChartSearchStatusPatientsPatientIdSearchSearchIdGet(
      patientId,
      searchId,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(patientId && searchId), ...queryOptions };
};

export type PollChartSearchStatusPatientsPatientIdSearchSearchIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>
>;
export type PollChartSearchStatusPatientsPatientIdSearchSearchIdGetQueryError = ErrorType<
  ErrorResponse | HTTPValidationError
>;

/**
 * @summary Poll Chart Search Status
 */
export const usePollChartSearchStatusPatientsPatientIdSearchSearchIdGet = <
  TData = Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>,
  TError = ErrorType<ErrorResponse | HTTPValidationError>,
>(
  patientId: number,
  searchId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof pollChartSearchStatusPatientsPatientIdSearchSearchIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getPollChartSearchStatusPatientsPatientIdSearchSearchIdGetQueryOptions(
    patientId,
    searchId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Retrieve all feedback records for a given search (likely one).
 * @summary List Feedback For Search
 */
export const listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet = (
  patientId: number,
  searchId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<FeedbackRecord[]>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/search/${searchId}/feedback`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGetQueryKey = (
  patientId: number,
  searchId: string,
) => [`/gw/chart-assist-api/patients/${patientId}/search/${searchId}/feedback`] as const;

export const getListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  searchId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGetQueryKey(patientId, searchId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>>
  > = ({ signal }) =>
    listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet(
      patientId,
      searchId,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!(patientId && searchId), ...queryOptions };
};

export type ListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>>
  >;
export type ListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary List Feedback For Search
 */
export const useListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet = <
  TData = Awaited<
    ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  searchId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getListFeedbackForSearchPatientsPatientIdSearchSearchIdFeedbackGetQueryOptions(
      patientId,
      searchId,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Create a new feedback record for a chart-assist search.
 * @summary Create Feedback
 */
export const createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost = (
  patientId: number,
  searchId: string,
  feedbackCreateRequest: FeedbackCreateRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<FeedbackCreateResponse>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/search/${searchId}/feedback`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: feedbackCreateRequest,
    },
    options,
  );
};

export const getCreateFeedbackPatientsPatientIdSearchSearchIdFeedbackPostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost>>,
    TError,
    { patientId: number; searchId: string; data: FeedbackCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost>>,
  TError,
  { patientId: number; searchId: string; data: FeedbackCreateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost>>,
    { patientId: number; searchId: string; data: FeedbackCreateRequest }
  > = (props) => {
    const { patientId, searchId, data } = props ?? {};

    return createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost(
      patientId,
      searchId,
      data,
      requestOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateFeedbackPatientsPatientIdSearchSearchIdFeedbackPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost>>
>;
export type CreateFeedbackPatientsPatientIdSearchSearchIdFeedbackPostMutationBody =
  FeedbackCreateRequest;
export type CreateFeedbackPatientsPatientIdSearchSearchIdFeedbackPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Create Feedback
 */
export const useCreateFeedbackPatientsPatientIdSearchSearchIdFeedbackPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createFeedbackPatientsPatientIdSearchSearchIdFeedbackPost>>,
    TError,
    { patientId: number; searchId: string; data: FeedbackCreateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getCreateFeedbackPatientsPatientIdSearchSearchIdFeedbackPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Delete an existing feedback entry (un-submit feedback).
 * @summary Delete Feedback
 */
export const deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete = (
  patientId: number,
  searchId: string,
  feedbackId: number,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/search/${searchId}/feedback/${feedbackId}`,
      method: 'delete',
    },
    options,
  );
};

export const getDeleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDeleteMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete>
      >,
      TError,
      { patientId: number; searchId: string; feedbackId: number },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete>
    >,
    TError,
    { patientId: number; searchId: string; feedbackId: number },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete>
      >,
      { patientId: number; searchId: string; feedbackId: number }
    > = (props) => {
      const { patientId, searchId, feedbackId } = props ?? {};

      return deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete(
        patientId,
        searchId,
        feedbackId,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type DeleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDeleteMutationResult =
  NonNullable<
    Awaited<
      ReturnType<typeof deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete>
    >
  >;

export type DeleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDeleteMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Delete Feedback
 */
export const useDeleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof deleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDelete>
    >,
    TError,
    { patientId: number; searchId: string; feedbackId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getDeleteFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdDeleteMutationOptions(
      options,
    );

  return useMutation(mutationOptions);
};

/**
 * Update an existing feedback entry with reasons
and/or free-text feedback, or change thumbs.
 * @summary Update Feedback
 */
export const updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch = (
  patientId: number,
  searchId: string,
  feedbackId: number,
  feedbackUpdateRequest: FeedbackUpdateRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>(
    {
      url: `/gw/chart-assist-api/patients/${patientId}/search/${searchId}/feedback/${feedbackId}`,
      method: 'patch',
      headers: { 'Content-Type': 'application/json' },
      data: feedbackUpdateRequest,
    },
    options,
  );
};

export const getUpdateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatchMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch>
      >,
      TError,
      { patientId: number; searchId: string; feedbackId: number; data: FeedbackUpdateRequest },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<
      ReturnType<typeof updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch>
    >,
    TError,
    { patientId: number; searchId: string; feedbackId: number; data: FeedbackUpdateRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch>
      >,
      { patientId: number; searchId: string; feedbackId: number; data: FeedbackUpdateRequest }
    > = (props) => {
      const { patientId, searchId, feedbackId, data } = props ?? {};

      return updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch(
        patientId,
        searchId,
        feedbackId,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type UpdateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatchMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch>>
  >;
export type UpdateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatchMutationBody =
  FeedbackUpdateRequest;
export type UpdateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatchMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Update Feedback
 */
export const useUpdateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<typeof updateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatch>
    >,
    TError,
    { patientId: number; searchId: string; feedbackId: number; data: FeedbackUpdateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getUpdateFeedbackPatientsPatientIdSearchSearchIdFeedbackFeedbackIdPatchMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Single blanket PREVENT endpoint (form or JSON body).

Send 11 required fields plus any optional uacr, hba1c. Equation is chosen from
optionals: base (none), uacr-only, hba1c-only, or full (both). Notes when
risks unavailable (e.g. age > 59).
 * @summary Calculate
 */
export const calculatePreventCalculatePost = (
  preventCalculateRequest: PreventCalculateRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<PreventCalculateResponse>(
    {
      url: `/gw/chart-assist-api/prevent/calculate`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: preventCalculateRequest,
    },
    options,
  );
};

export const getCalculatePreventCalculatePostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof calculatePreventCalculatePost>>,
    TError,
    { data: PreventCalculateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof calculatePreventCalculatePost>>,
  TError,
  { data: PreventCalculateRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof calculatePreventCalculatePost>>,
    { data: PreventCalculateRequest }
  > = (props) => {
    const { data } = props ?? {};

    return calculatePreventCalculatePost(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CalculatePreventCalculatePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof calculatePreventCalculatePost>>
>;
export type CalculatePreventCalculatePostMutationBody = PreventCalculateRequest;
export type CalculatePreventCalculatePostMutationError = ErrorType<HTTPValidationError>;

/**
 * @summary Calculate
 */
export const useCalculatePreventCalculatePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof calculatePreventCalculatePost>>,
    TError,
    { data: PreventCalculateRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions = getCalculatePreventCalculatePostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Delete User Settings
 */
export const deleteUserSettingsSettingsDelete = (
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<void>({ url: `/gw/chart-assist-api/settings`, method: 'delete' }, options);
};

export const getDeleteUserSettingsSettingsDeleteMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUserSettingsSettingsDelete>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteUserSettingsSettingsDelete>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteUserSettingsSettingsDelete>>,
    TVariables
  > = () => {
    return deleteUserSettingsSettingsDelete(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserSettingsSettingsDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteUserSettingsSettingsDelete>>
>;

export type DeleteUserSettingsSettingsDeleteMutationError = ErrorType<unknown>;

/**
 * @summary Delete User Settings
 */
export const useDeleteUserSettingsSettingsDelete = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteUserSettingsSettingsDelete>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions = getDeleteUserSettingsSettingsDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Get AI settings for the authenticated user as a JSON blob.
 * @summary Get User Settings
 */
export const getUserSettingsSettingsGet = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<AIUserSettingsRecord>(
    { url: `/gw/chart-assist-api/settings`, method: 'get', signal },
    options,
  );
};

export const getGetUserSettingsSettingsGetQueryKey = () =>
  [`/gw/chart-assist-api/settings`] as const;

export const getGetUserSettingsSettingsGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserSettingsSettingsGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getUserSettingsSettingsGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryOptions<Awaited<ReturnType<typeof getUserSettingsSettingsGet>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserSettingsSettingsGetQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserSettingsSettingsGet>>> = ({
    signal,
  }) => getUserSettingsSettingsGet(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUserSettingsSettingsGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserSettingsSettingsGet>>
>;
export type GetUserSettingsSettingsGetQueryError = ErrorType<unknown>;

/**
 * @summary Get User Settings
 */
export const useGetUserSettingsSettingsGet = <
  TData = Awaited<ReturnType<typeof getUserSettingsSettingsGet>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getUserSettingsSettingsGet>>, TError, TData>;
  request?: SecondParameter<typeof customInstance>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUserSettingsSettingsGetQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Create or update the AI settings JSON blob for the authenticated user.
 * @summary Upsert User Settings
 */
export const upsertUserSettingsSettingsPut = (
  aIUserSettingsUpsertRequest: AIUserSettingsUpsertRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<AIUserSettingsRecord>(
    {
      url: `/gw/chart-assist-api/settings`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: aIUserSettingsUpsertRequest,
    },
    options,
  );
};

export const getUpsertUserSettingsSettingsPutMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upsertUserSettingsSettingsPut>>,
    TError,
    { data: AIUserSettingsUpsertRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof upsertUserSettingsSettingsPut>>,
  TError,
  { data: AIUserSettingsUpsertRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof upsertUserSettingsSettingsPut>>,
    { data: AIUserSettingsUpsertRequest }
  > = (props) => {
    const { data } = props ?? {};

    return upsertUserSettingsSettingsPut(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpsertUserSettingsSettingsPutMutationResult = NonNullable<
  Awaited<ReturnType<typeof upsertUserSettingsSettingsPut>>
>;
export type UpsertUserSettingsSettingsPutMutationBody = AIUserSettingsUpsertRequest;
export type UpsertUserSettingsSettingsPutMutationError = ErrorType<HTTPValidationError>;

/**
 * @summary Upsert User Settings
 */
export const useUpsertUserSettingsSettingsPut = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof upsertUserSettingsSettingsPut>>,
    TError,
    { data: AIUserSettingsUpsertRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions = getUpsertUserSettingsSettingsPutMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Stream chart search results via Server-Sent Events.
 * @summary Stream Chart Search Results
 */
export const streamChartSearchResultsStreamSearchIdGet = (
  searchId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/stream/${searchId}`, method: 'get', signal },
    options,
  );
};

export const getStreamChartSearchResultsStreamSearchIdGetQueryKey = (searchId: string) =>
  [`/gw/chart-assist-api/stream/${searchId}`] as const;

export const getStreamChartSearchResultsStreamSearchIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>,
  TError = ErrorType<HTTPValidationError | ErrorResponse>,
>(
  searchId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getStreamChartSearchResultsStreamSearchIdGetQueryKey(searchId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>
  > = ({ signal }) => streamChartSearchResultsStreamSearchIdGet(searchId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!searchId, ...queryOptions };
};

export type StreamChartSearchResultsStreamSearchIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>
>;
export type StreamChartSearchResultsStreamSearchIdGetQueryError = ErrorType<
  HTTPValidationError | ErrorResponse
>;

/**
 * @summary Stream Chart Search Results
 */
export const useStreamChartSearchResultsStreamSearchIdGet = <
  TData = Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>,
  TError = ErrorType<HTTPValidationError | ErrorResponse>,
>(
  searchId: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof streamChartSearchResultsStreamSearchIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getStreamChartSearchResultsStreamSearchIdGetQueryOptions(searchId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Triggers the DaprTimeoutClient's test_sleep_to_ensure_pod_kills_itself method.
This will intentionally cause a Dapr client timeout and pod self-termination.

WARNING: This endpoint is only available in local/dev environments.
 * @summary Trigger test DaprTimeoutClient timeout (kills pod on success)
 */
export const triggerTestDaprTimeoutClientTestDaprTimeoutClientPost = (
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/test-dapr-timeout-client`, method: 'post' },
    options,
  );
};

export const getTriggerTestDaprTimeoutClientTestDaprTimeoutClientPostMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof triggerTestDaprTimeoutClientTestDaprTimeoutClientPost>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof triggerTestDaprTimeoutClientTestDaprTimeoutClientPost>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof triggerTestDaprTimeoutClientTestDaprTimeoutClientPost>>,
    TVariables
  > = () => {
    return triggerTestDaprTimeoutClientTestDaprTimeoutClientPost(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type TriggerTestDaprTimeoutClientTestDaprTimeoutClientPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof triggerTestDaprTimeoutClientTestDaprTimeoutClientPost>>
>;

export type TriggerTestDaprTimeoutClientTestDaprTimeoutClientPostMutationError = ErrorType<unknown>;

/**
 * @summary Trigger test DaprTimeoutClient timeout (kills pod on success)
 */
export const useTriggerTestDaprTimeoutClientTestDaprTimeoutClientPost = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof triggerTestDaprTimeoutClientTestDaprTimeoutClientPost>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getTriggerTestDaprTimeoutClientTestDaprTimeoutClientPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Triggers the async_wf_client's test_sleep_to_ensure_pod_kills_itself method.
This will intentionally cause a workflow client timeout and pod self-termination.

WARNING: This endpoint is only available in local/dev environments.
 * @summary Trigger test workflow client timeout (kills pod on success)
 */
export const triggerTestWorkflowTimeoutTestWorkflowTimeoutPost = (
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/test-workflow-timeout`, method: 'post' },
    options,
  );
};

export const getTriggerTestWorkflowTimeoutTestWorkflowTimeoutPostMutationOptions = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof triggerTestWorkflowTimeoutTestWorkflowTimeoutPost>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof triggerTestWorkflowTimeoutTestWorkflowTimeoutPost>>,
  TError,
  TVariables,
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof triggerTestWorkflowTimeoutTestWorkflowTimeoutPost>>,
    TVariables
  > = () => {
    return triggerTestWorkflowTimeoutTestWorkflowTimeoutPost(requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type TriggerTestWorkflowTimeoutTestWorkflowTimeoutPostMutationResult = NonNullable<
  Awaited<ReturnType<typeof triggerTestWorkflowTimeoutTestWorkflowTimeoutPost>>
>;

export type TriggerTestWorkflowTimeoutTestWorkflowTimeoutPostMutationError = ErrorType<unknown>;

/**
 * @summary Trigger test workflow client timeout (kills pod on success)
 */
export const useTriggerTestWorkflowTimeoutTestWorkflowTimeoutPost = <
  TError = ErrorType<unknown>,
  TVariables = void,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof triggerTestWorkflowTimeoutTestWorkflowTimeoutPost>>,
    TError,
    TVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getTriggerTestWorkflowTimeoutTestWorkflowTimeoutPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Delete all index-related data for a patient (for test cleanup).
 * @summary Delete Patient Index Data
 */
export const deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/testing/patients/${patientId}/index-data`, method: 'delete' },
    options,
  );
};

export const getDeletePatientIndexDataTestingPatientsPatientIdIndexDataDeleteMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete>>,
    TError,
    { patientId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete>>,
  TError,
  { patientId: number },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete>>,
    { patientId: number }
  > = (props) => {
    const { patientId } = props ?? {};

    return deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete(patientId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeletePatientIndexDataTestingPatientsPatientIdIndexDataDeleteMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete>>
  >;

export type DeletePatientIndexDataTestingPatientsPatientIdIndexDataDeleteMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Delete Patient Index Data
 */
export const useDeletePatientIndexDataTestingPatientsPatientIdIndexDataDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deletePatientIndexDataTestingPatientsPatientIdIndexDataDelete>>,
    TError,
    { patientId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getDeletePatientIndexDataTestingPatientsPatientIdIndexDataDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Get Patient Index Status
 */
export const getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/testing/patients/${patientId}/index-status`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPatientIndexStatusTestingPatientsPatientIdIndexStatusGetQueryKey = (
  patientId: number,
) => [`/gw/chart-assist-api/testing/patients/${patientId}/index-status`] as const;

export const getGetPatientIndexStatusTestingPatientsPatientIdIndexStatusGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPatientIndexStatusTestingPatientsPatientIdIndexStatusGetQueryKey(patientId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>
  > = ({ signal }) =>
    getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet(patientId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!patientId, ...queryOptions };
};

export type GetPatientIndexStatusTestingPatientsPatientIdIndexStatusGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>
>;
export type GetPatientIndexStatusTestingPatientsPatientIdIndexStatusGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Patient Index Status
 */
export const useGetPatientIndexStatusTestingPatientsPatientIdIndexStatusGet = <
  TData = Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPatientIndexStatusTestingPatientsPatientIdIndexStatusGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetPatientIndexStatusTestingPatientsPatientIdIndexStatusGetQueryOptions(
    patientId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Patient Json Blob Index
 */
export const getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/testing/patients/${patientId}/json-blob-index`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGetQueryKey = (
  patientId: number,
) => [`/gw/chart-assist-api/testing/patients/${patientId}/json-blob-index`] as const;

export const getGetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGetQueryKey(patientId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>>
  > = ({ signal }) =>
    getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet(
      patientId,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!patientId, ...queryOptions };
};

export type GetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>>
  >;
export type GetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Patient Json Blob Index
 */
export const useGetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet = <
  TData = Awaited<
    ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexGetQueryOptions(
      patientId,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Insert a legacy JSON blob into patient_clinical_insights_index.
 * @summary Insert Patient Json Blob Index
 */
export const insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost = (
  patientId: number,
  insertJsonBlobRequest: InsertJsonBlobRequest,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/testing/patients/${patientId}/json-blob-index`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: insertJsonBlobRequest,
    },
    options,
  );
};

export const getInsertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPostMutationOptions =
  <TError = ErrorType<HTTPValidationError>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<typeof insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost>
      >,
      TError,
      { patientId: number; data: InsertJsonBlobRequest },
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  }): UseMutationOptions<
    Awaited<ReturnType<typeof insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost>>,
    TError,
    { patientId: number; data: InsertJsonBlobRequest },
    TContext
  > => {
    const { mutation: mutationOptions, request: requestOptions } = options ?? {};

    const mutationFn: MutationFunction<
      Awaited<
        ReturnType<typeof insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost>
      >,
      { patientId: number; data: InsertJsonBlobRequest }
    > = (props) => {
      const { patientId, data } = props ?? {};

      return insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost(
        patientId,
        data,
        requestOptions,
      );
    };

    return { mutationFn, ...mutationOptions };
  };

export type InsertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPostMutationResult =
  NonNullable<
    Awaited<ReturnType<typeof insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost>>
  >;
export type InsertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPostMutationBody =
  InsertJsonBlobRequest;
export type InsertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Insert Patient Json Blob Index
 */
export const useInsertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof insertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPost>>,
    TError,
    { patientId: number; data: InsertJsonBlobRequest },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getInsertPatientJsonBlobIndexTestingPatientsPatientIdJsonBlobIndexPostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Get Patient Nonvisit Note Cache
 */
export const getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/testing/patients/${patientId}/nonvisit-note-cache`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGetQueryKey = (
  patientId: number,
) => [`/gw/chart-assist-api/testing/patients/${patientId}/nonvisit-note-cache`] as const;

export const getGetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGetQueryOptions =
  <
    TData = Awaited<
      ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
    >,
    TError = ErrorType<HTTPValidationError>,
  >(
    patientId: number,
    options?: {
      query?: UseQueryOptions<
        Awaited<
          ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
        >,
        TError,
        TData
      >;
      request?: SecondParameter<typeof customInstance>;
    },
  ): UseQueryOptions<
    Awaited<
      ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
    >,
    TError,
    TData
  > & { queryKey: QueryKey } => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGetQueryKey(patientId);

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
      >
    > = ({ signal }) =>
      getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet(
        patientId,
        requestOptions,
        signal,
      );

    return { queryKey, queryFn, enabled: !!patientId, ...queryOptions };
  };

export type GetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGetQueryResult =
  NonNullable<
    Awaited<
      ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
    >
  >;
export type GetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Patient Nonvisit Note Cache
 */
export const useGetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet = <
  TData = Awaited<
    ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<
        ReturnType<typeof getPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGet>
      >,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetPatientNonvisitNoteCacheTestingPatientsPatientIdNonvisitNoteCacheGetQueryOptions(
      patientId,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Patient Visit Note Cache
 */
export const getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet = (
  patientId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/testing/patients/${patientId}/visit-note-cache`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGetQueryKey = (
  patientId: number,
) => [`/gw/chart-assist-api/testing/patients/${patientId}/visit-note-cache`] as const;

export const getGetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGetQueryOptions = <
  TData = Awaited<
    ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGetQueryKey(patientId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>>
  > = ({ signal }) =>
    getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet(
      patientId,
      requestOptions,
      signal,
    );

  return { queryKey, queryFn, enabled: !!patientId, ...queryOptions };
};

export type GetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGetQueryResult =
  NonNullable<
    Awaited<ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>>
  >;
export type GetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Patient Visit Note Cache
 */
export const useGetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet = <
  TData = Awaited<
    ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>
  >,
  TError = ErrorType<HTTPValidationError>,
>(
  patientId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    getGetPatientVisitNoteCacheTestingPatientsPatientIdVisitNoteCacheGetQueryOptions(
      patientId,
      options,
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Delete all OCR data for a report.
 * @summary Delete Ocr Report Data
 */
export const deleteOcrReportDataTestingReportsReportIdDelete = (
  reportId: number,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/testing/reports/${reportId}`, method: 'delete' },
    options,
  );
};

export const getDeleteOcrReportDataTestingReportsReportIdDeleteMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOcrReportDataTestingReportsReportIdDelete>>,
    TError,
    { reportId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOcrReportDataTestingReportsReportIdDelete>>,
  TError,
  { reportId: number },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOcrReportDataTestingReportsReportIdDelete>>,
    { reportId: number }
  > = (props) => {
    const { reportId } = props ?? {};

    return deleteOcrReportDataTestingReportsReportIdDelete(reportId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOcrReportDataTestingReportsReportIdDeleteMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteOcrReportDataTestingReportsReportIdDelete>>
>;

export type DeleteOcrReportDataTestingReportsReportIdDeleteMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Delete Ocr Report Data
 */
export const useDeleteOcrReportDataTestingReportsReportIdDelete = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOcrReportDataTestingReportsReportIdDelete>>,
    TError,
    { reportId: number },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getDeleteOcrReportDataTestingReportsReportIdDeleteMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * @summary Get Ocr Report
 */
export const getOcrReportTestingReportsReportIdGet = (
  reportId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/testing/reports/${reportId}`, method: 'get', signal },
    options,
  );
};

export const getGetOcrReportTestingReportsReportIdGetQueryKey = (reportId: number) =>
  [`/gw/chart-assist-api/testing/reports/${reportId}`] as const;

export const getGetOcrReportTestingReportsReportIdGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  reportId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOcrReportTestingReportsReportIdGetQueryKey(reportId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>
  > = ({ signal }) => getOcrReportTestingReportsReportIdGet(reportId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!reportId, ...queryOptions };
};

export type GetOcrReportTestingReportsReportIdGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>
>;
export type GetOcrReportTestingReportsReportIdGetQueryError = ErrorType<HTTPValidationError>;

/**
 * @summary Get Ocr Report
 */
export const useGetOcrReportTestingReportsReportIdGet = <
  TData = Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  reportId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOcrReportTestingReportsReportIdGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetOcrReportTestingReportsReportIdGetQueryOptions(reportId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Get Ocr Report Files
 */
export const getOcrReportFilesTestingReportsReportIdFilesGet = (
  reportId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/testing/reports/${reportId}/files`, method: 'get', signal },
    options,
  );
};

export const getGetOcrReportFilesTestingReportsReportIdFilesGetQueryKey = (reportId: number) =>
  [`/gw/chart-assist-api/testing/reports/${reportId}/files`] as const;

export const getGetOcrReportFilesTestingReportsReportIdFilesGetQueryOptions = <
  TData = Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  reportId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOcrReportFilesTestingReportsReportIdFilesGetQueryKey(reportId);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>
  > = ({ signal }) =>
    getOcrReportFilesTestingReportsReportIdFilesGet(reportId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!reportId, ...queryOptions };
};

export type GetOcrReportFilesTestingReportsReportIdFilesGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>
>;
export type GetOcrReportFilesTestingReportsReportIdFilesGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Get Ocr Report Files
 */
export const useGetOcrReportFilesTestingReportsReportIdFilesGet = <
  TData = Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  reportId: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOcrReportFilesTestingReportsReportIdFilesGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetOcrReportFilesTestingReportsReportIdFilesGetQueryOptions(
    reportId,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Terminate and purge a Dapr workflow instance via the Dapr SDK.
 * @summary Purge Workflow
 */
export const purgeWorkflowTestingWorkflowsInstanceIdPurgePost = (
  instanceId: string,
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<unknown>(
    { url: `/gw/chart-assist-api/testing/workflows/${instanceId}/purge`, method: 'post' },
    options,
  );
};

export const getPurgeWorkflowTestingWorkflowsInstanceIdPurgePostMutationOptions = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof purgeWorkflowTestingWorkflowsInstanceIdPurgePost>>,
    TError,
    { instanceId: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof purgeWorkflowTestingWorkflowsInstanceIdPurgePost>>,
  TError,
  { instanceId: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof purgeWorkflowTestingWorkflowsInstanceIdPurgePost>>,
    { instanceId: string }
  > = (props) => {
    const { instanceId } = props ?? {};

    return purgeWorkflowTestingWorkflowsInstanceIdPurgePost(instanceId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PurgeWorkflowTestingWorkflowsInstanceIdPurgePostMutationResult = NonNullable<
  Awaited<ReturnType<typeof purgeWorkflowTestingWorkflowsInstanceIdPurgePost>>
>;

export type PurgeWorkflowTestingWorkflowsInstanceIdPurgePostMutationError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Purge Workflow
 */
export const usePurgeWorkflowTestingWorkflowsInstanceIdPurgePost = <
  TError = ErrorType<HTTPValidationError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof purgeWorkflowTestingWorkflowsInstanceIdPurgePost>>,
    TError,
    { instanceId: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions =
    getPurgeWorkflowTestingWorkflowsInstanceIdPurgePostMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Wait for a Dapr workflow to reach a terminal state and return its status.

Polls the Dapr HTTP API directly instead of using async_wf_client because
the SDK wrapper enforces a 10-second timeout on all calls (to detect frozen
gRPC connections in production).  That's too short for waiting on a workflow
that legitimately takes 30-60s to complete.  The Dapr HTTP API has no such
wrapper, so we can poll for as long as the test needs.

Retries on 404 (workflow not yet registered by the Dapr scheduler — the
workflow is triggered asynchronously via Kafka) so callers don't need to
implement their own retry loop.

If the underlying workflow itself reaches FAILED or TERMINATED, that is
reported via ``runtime_status`` in the 200 response.  A 408 means this
endpoint's own deadline expired before the workflow reached *any* terminal
state.
 * @summary Wait For Workflow
 */
export const waitForWorkflowTestingWorkflowsInstanceIdWaitGet = (
  instanceId: string,
  params?: WaitForWorkflowTestingWorkflowsInstanceIdWaitGetParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    {
      url: `/gw/chart-assist-api/testing/workflows/${instanceId}/wait`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getWaitForWorkflowTestingWorkflowsInstanceIdWaitGetQueryKey = (
  instanceId: string,
  params?: WaitForWorkflowTestingWorkflowsInstanceIdWaitGetParams,
) =>
  [
    `/gw/chart-assist-api/testing/workflows/${instanceId}/wait`,
    ...(params ? [params] : []),
  ] as const;

export const getWaitForWorkflowTestingWorkflowsInstanceIdWaitGetQueryOptions = <
  TData = Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  instanceId: string,
  params?: WaitForWorkflowTestingWorkflowsInstanceIdWaitGetParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getWaitForWorkflowTestingWorkflowsInstanceIdWaitGetQueryKey(instanceId, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>
  > = ({ signal }) =>
    waitForWorkflowTestingWorkflowsInstanceIdWaitGet(instanceId, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!instanceId, ...queryOptions };
};

export type WaitForWorkflowTestingWorkflowsInstanceIdWaitGetQueryResult = NonNullable<
  Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>
>;
export type WaitForWorkflowTestingWorkflowsInstanceIdWaitGetQueryError =
  ErrorType<HTTPValidationError>;

/**
 * @summary Wait For Workflow
 */
export const useWaitForWorkflowTestingWorkflowsInstanceIdWaitGet = <
  TData = Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>,
  TError = ErrorType<HTTPValidationError>,
>(
  instanceId: string,
  params?: WaitForWorkflowTestingWorkflowsInstanceIdWaitGetParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof waitForWorkflowTestingWorkflowsInstanceIdWaitGet>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getWaitForWorkflowTestingWorkflowsInstanceIdWaitGetQueryOptions(
    instanceId,
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};
